import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Output,
  QueryList,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { OKTA_AUTH } from '@okta/okta-angular';
import { CommonModule } from '@angular/common';
import { UserStore } from '@shared/stores/user.store';
import { MatDialogRef } from '@angular/material/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { filter } from 'rxjs';
import { MatRipple } from '@angular/material/core';
import { NMFTAButtonComponent, NMFTAIconComponent } from '@nmfta/nmfta-ui';

@Component({
    selector: 'nmfta-nav-bar',
    imports: [
        RouterModule,
        TranslateModule,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NMFTAButtonComponent,
        CommonModule,
        NMFTAIconComponent,
        NgxSkeletonLoaderModule,
        MatAccordion,
        MatExpansionModule,
        MatRipple,
    ],
    templateUrl: './nav-bar.component.html',
    styleUrl: './nav-bar.component.scss'
})
export class NavBarComponent {
  userStore = inject(UserStore);
  oktaAuth = inject(OKTA_AUTH);
  private readonly router = inject(Router);
  protected readonly panelOpenState = signal(false);
  protected readonly subPanelOpenState = signal(false);
  protected readonly hasUserInformation$: Signal<boolean> = this.userStore.hasUserInformation;
  protected readonly isClassificationManager$: Signal<boolean> = this.userStore.isClassificationManager;

  @ViewChild('nmfcBookModalTemplate', { static: true }) nmfcBookModalTemplate!: TemplateRef<unknown>;
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;
  @ViewChildren('subPanel') subPanel!: QueryList<MatExpansionPanel>;
  @Output() readonly downloadsClicked = new EventEmitter<void>();

  langList = [
    { code: 'en', label: 'ENG', language: 'English' },
    { code: 'fr', label: 'FRE', language: 'French' },
    { code: 'es', label: 'ESP', language: 'Spanish' },
  ];

  langLabel: string;
  langSelected: string;
  isSmallScreen = false;
  user$ = this.userStore.user;

  dialogRef: MatDialogRef<any>;

  isOpen = false;
  private currentUrl$ = signal(this.router.url);


  readonly userLoading$ = this.userStore.userLoading;
  readonly isOktaLoginCallback$ = computed(() => {
    const url = this.currentUrl$();
    return url === '/' || url.startsWith('/login/callback');
  });


  constructor(
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
  ) {
    effect(() => {
      const isOpen = this.panelOpenState();
        this.subPanel?.forEach((panel) => {
          if (panel.expanded) {
            panel.close();
          }
        });

        this.subPanelOpenState.set(false);
    })

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.currentUrl$.set(e.urlAfterRedirects);
      });
    this.langSelected = this.translate.getDefaultLang();
    this.langLabel = this.langList.find((lang) => lang.code === this.langSelected)!.label;
    this.breakpointObserver.observe('(min-width: 1200px)').subscribe((result) => {
      this.isSmallScreen = !result.matches;
      if (result.matches && this.isOpen) {
        this.closeAccordions();
        this.toggleNavMenu();
      }
    });
  }

  closeAccordions() {
    this.panels.forEach((panel) => {
      if (panel.expanded) {
        panel.close();
      }
    });
  }

  toggleNavMenu() {
    this.closeAccordions();
    this.isOpen = !this.isOpen;
  }

  useLanguage(code: string): void {
    this.langLabel = this.langList.find((lang) => lang.code === code)!.label;
    this.langSelected = code;
    this.translate.use(code);
  }

  public async signIn(): Promise<void> {
    await this.oktaAuth.signInWithRedirect();
  }

  public async signOut(): Promise<void> {
    await this.oktaAuth.signOut();
  }

  public routeToExternalLink(url: string): void {
    if (typeof window !== 'undefined') window.open(url, '_blank');
  }

  navigate(route: string) {
    if (this.isSmallScreen) {
      this.closeAccordions();
      this.toggleNavMenu();
    }
    this.router.navigate([route]);
  }

  openDownloadsPanel(): void {
    this.downloadsClicked.emit();

    if (this.isSmallScreen && this.isOpen) {
      this.toggleNavMenu();
    }
  }
}
